<template>
  <!-- Need to add height inherit because Vue 2 don't support multiple root ele -->
  <div style="height: inherit">
    <div
      class="body-content-overlay"
      :class="{'show': mqShallShowLeftSidebar}"
      @click="mqShallShowLeftSidebar=false"
    />

    <!-- Main Area -->
    <section class="chat-app-window">
      <rc-overlay
        :show="loading"
        no-fade
        variant="white"
        class="h-100"
      >

        <div
          v-if="!customer.id && !addingCustomer && !loading"
          class="start-chat-area"
        >
          <div class="mb-1 start-chat-icon">
            <feather-icon
              icon="UserIcon"
              size="56"
            />
          </div>
          <div>
            <span
              class="sidebar-toggle start-chat-text text-nowrap "
              :class="{'app-tour-customers-show-sidebar-button': !(mqShallShowLeftSidebar || largeScreen)}"
              @click="startConversation"
            >{{ $t('customers.select-customer') }}</span>
            <span> {{ $t('shared.or') }} </span>
            <span
              class="start-chat-text text-nowrap"
              @click="initAddCustomer"
            >{{ $t('customers.add-new-customer') }}</span>
          </div>
        </div>

        <!-- Customer Content -->
        <div
          v-else-if="customer.id || addingCustomer"
          class="active-chat"
        >
          <!-- Customer Navbar -->
          <div class="chat-navbar">
            <header class="chat-header">

              <!-- Avatar & Name -->
              <div class="d-flex align-items-center">

                <!-- Toggle Icon -->
                <div class="sidebar-toggle d-block d-lg-none mr-1">
                  <feather-icon
                    icon="MenuIcon"
                    class="cursor-pointer"
                    size="21"
                    @click="mqShallShowLeftSidebar = true"
                  />
                </div>

                <b-avatar
                  v-if="!addingCustomer"
                  size="36"
                  class="mr-50"
                >
                  {{ currentCustomerInitials }}
                </b-avatar>
                <h6
                  v-if="!addingCustomer"
                  class="mb-0"
                >
                  {{ customer.lastName }} {{ customer.firstName }}<br>
                  <b-link
                    v-if="customer.phone"
                    :href="`tel:${customer.phone}`"
                    class="text-muted font-small-3"
                  >{{ customer.phone }}</b-link>
                  <span
                    v-else-if="customer.guardianPhone"
                    class="text-muted font-small-3"
                  >
                    {{ $t('customers.guardian') }}<span
                      v-if="customer.guardianLastName"
                      class="font-weight-bold"
                    >
                      {{ customer.guardianLastName }}</span><span
                      v-if="customer.guardianFirstName"
                      class="font-weight-bold"
                    > {{ customer.guardianFirstName }}</span>:
                    <b-link
                      :href="`tel:${customer.guardianPhone}`"
                    >{{ customer.guardianPhone }}</b-link>
                  </span>
                </h6>
              </div>

              <!-- Customer Actions -->
              <div class="d-flex align-items-center">

                <b-dropdown
                  v-if="inviteButtonVisible && hasBothEmails"
                  variant="link"
                  no-caret
                  toggle-class="p-0 mx-1"
                  right
                  class="d-sm-block d-none"
                >
                  <template #button-content>
                    <b-button variant="flat-primary">
                      <feather-icon
                        icon="UserPlusIcon"
                        size="17"
                      />
                      <span class="ml-50 d-none d-sm-inline-block">
                        {{ $t('customers.invite-to-totedi') }}
                      </span>
                    </b-button>
                  </template>
                  <b-dropdown-item @click="inviteCustomer('Student')">
                    {{ $t('customers.invite.student') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="inviteCustomer('Guardian')">
                    {{ $t('customers.invite.guardian') }}
                  </b-dropdown-item>
                </b-dropdown>
                <b-button
                  v-else-if="inviteButtonVisible"
                  variant="flat-primary"
                  class="mx-1 d-sm-block d-none"
                  @click="inviteCustomer(defaultInviteType)"
                >
                  <feather-icon
                    icon="UserPlusIcon"
                    size="17"
                  />
                  <span class="ml-50 d-none d-sm-inline-block">
                    {{ $t('customers.invite-to-totedi') }}
                  </span>
                </b-button>

                <b-link
                  v-if="customer.phone"
                  :href="`tel:${customer.phone}`"
                >
                  <feather-icon
                    id="call-customer-icon"
                    icon="PhoneCallIcon"
                    size="17"
                    class="d-sm-block d-none mr-1"
                  />
                  <b-tooltip
                    target="call-customer-icon"
                    noninteractive
                  >
                    {{ $t('shared.call') }}
                  </b-tooltip>
                </b-link>

                <b-dropdown
                  v-if="canSendCustomerMessage"
                  variant="link"
                  no-caret
                  toggle-class="p-0 mx-1"
                  right
                  class="d-sm-block d-none"
                >
                  <template #button-content>
                    <b-button
                      :id="sendMessageButtonId"
                      variant="flat-primary"
                      class="btn-icon"
                    >
                      <feather-icon
                        icon="MailIcon"
                        size="17"
                      />
                    </b-button>
                  </template>
                  <b-tooltip
                    :target="sendMessageButtonId"
                    noninteractive
                  >
                    {{ $t('shared.send-message') }}
                  </b-tooltip>
                  <template v-if="messageReceivers.length && parseInt(statistics.incomeToPay)">
                    <h6 class="dropdown-header text-uppercase text-muted">
                      {{ $t('customers.send-message.payment-reminder') }}
                    </h6>
                    <b-dropdown-item
                      v-for="receiver in messageReceivers"
                      :key="`payment-reminder-${receiver.type}`"
                      :disabled="sendingPaymentReminder"
                      @click="sendPaymentReminder(receiver.type)"
                    >
                      {{ $t('customers.send-message.payment-reminder') }}
                      <span v-if="messageReceivers.length > 1">
                        - {{ $t(receiver.label) }}
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-divider />
                  </template>
                  <b-dropdown-item @click="showCustomMessageModal()">
                    {{ $t('customers.send-message.custom') }}
                  </b-dropdown-item>
                </b-dropdown>

                <b-button
                  v-acl="[role.admin]"
                  variant="flat-primary"
                  class="mx-1 d-sm-block d-none app-tour-customer-edit-button"
                  @click="toggleEditMode"
                >
                  <feather-icon
                    id="edit-view-customer-toggle-icon"
                    :icon="editMode === true ? 'EyeIcon' : 'Edit2Icon'"
                    size="17"
                    class="d-sm-block d-none"
                  />
                  <b-tooltip
                    target="edit-view-customer-toggle-icon"
                    noninteractive
                  >
                    {{ editMode === true ? $t('shared.view') : $t('shared.edit') }}
                  </b-tooltip>
                </b-button>

                <b-button
                  v-if="editMode"
                  variant="success"
                  class="mr-1 mr-sm-0 app-tour-customers-save-button"
                  @click="$refs.customerForm.saveCustomer()"
                >
                  <feather-icon icon="SaveIcon" />
                  <span class="ml-50 d-none d-sm-inline-block">
                    {{ $t('shared.btn.save') }}
                  </span>
                </b-button>

                <div class="dropdown d-sm-none d-block">
                  <b-dropdown
                    variant="link"
                    no-caret
                    toggle-class="p-0"
                    right
                    class="app-tour-customers-customer-menu-icon"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="17"
                        class="align-middle text-body"
                      />
                    </template>
                    <b-dropdown-item
                      v-if="inviteButtonVisible && customer.email"
                      class="d-sm-none d-block"
                      @click="inviteCustomer('Student')"
                    >
                      {{ $t('customers.invite.student') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="inviteButtonVisible && customer.guardianEmail"
                      class="d-sm-none d-block"
                      @click="inviteCustomer('Guardian')"
                    >
                      {{ $t('customers.invite.guardian') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="customer.phone"
                      :href="`tel:${customer.phone}`"
                      class="d-sm-none d-block"
                    >
                      {{ $t('shared.call') }}
                    </b-dropdown-item>
                    <template v-if="canSendCustomerMessage && messageReceivers.length > 1">
                      <b-dropdown
                        variant="link"
                        dropright
                        no-caret
                        toggle-class="dropdown-item d-sm-none d-block d-flex justify-content-between align-items-center w-100"
                        class="dropdown-submenu"
                      >
                        <template #button-content>
                          <span>{{ $t('customers.send-message.payment-reminder') }}</span>
                          <feather-icon
                            icon="ChevronRightIcon"
                            size="14"
                          />
                        </template>
                        <b-dropdown-item
                          v-for="receiver in messageReceivers"
                          :key="`sm-payment-reminder-${receiver.type}`"
                          :disabled="sendingPaymentReminder"
                          @click="sendPaymentReminder(receiver.type)"
                        >
                          {{ $t('customers.send-message.payment-reminder-send', { receiver: $t(receiver.label) }) }}
                        </b-dropdown-item>
                      </b-dropdown>
                      <b-dropdown-item
                        class="d-sm-none d-block"
                        @click="showCustomMessageModal()"
                      >
                        {{ $t('customers.send-message.custom') }}
                      </b-dropdown-item>
                    </template>
                    <template v-else-if="canSendCustomerMessage && messageReceivers.length === 1">
                      <b-dropdown-item
                        class="d-sm-none d-block"
                        :disabled="sendingPaymentReminder"
                        @click="sendPaymentReminder(singleMessageReceiverType)"
                      >
                        {{ $t('customers.send-message.payment-reminder') }}
                      </b-dropdown-item>
                      <b-dropdown-item
                        class="d-sm-none d-block"
                        @click="showCustomMessageModal()"
                      >
                        {{ $t('customers.send-message.custom') }}
                      </b-dropdown-item>
                    </template>
                    <b-dropdown-divider class="d-sm-none d-block" />
                    <b-dropdown-item
                      class="d-sm-none d-block"
                      @click="toggleEditMode"
                    >
                      {{ editMode === true ? $t('shared.view') : $t('shared.edit') }}
                    </b-dropdown-item>
                  <!--                  <b-dropdown-item>-->
                  <!--                    Archive-->
                  <!--                  </b-dropdown-item>-->
                  </b-dropdown>
                </div>
              </div>
            </header>
          </div>

          <!-- User Chat Area -->
          <vue-perfect-scrollbar
            ref="refScroll"
            :settings="perfectScrollbarSettings"
            class="user-chats scroll-area"
          >
            <customer-form
              v-if="editMode"
              ref="customerForm"
              :customer="customer"
              @customer-added="customerAddedHandler"
              @customer-anonymized="customerAnonymizedHandler"
              @customer-saved="customerSavedHandler"
              @leaving-adding-form="addingCustomer = false"
              @content-updated="updateRefScroll"
            />
            <customer-view
              v-else
              :customer="customer"
              @content-updated="updateRefScroll"
            />
          </vue-perfect-scrollbar>
        </div>

      </rc-overlay>
    </section>

    <!-- Sidebar -->
    <portal to="content-renderer-sidebar-left">
      <customer-left-sidebar
        :customers="customers"
        :active-customer-id="customer ? customer.id : null"
        :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
        @open-customer-view="openCustomerView"
        @add-new-customer="initAddCustomer"
      />
    </portal>

    <send-message-modal
      v-model="isSendMessageModalVisible"
      :customer-id="customer ? customer.id : null"
      :receivers="messageReceivers"
      :default-receiver-type="customMessageDefaultReceiver"
    />
  </div>
</template>

<script>
import { computed, nextTick, ref } from '@vue/composition-api'
import {
  BAvatar, BButton, BDropdown, BDropdownDivider, BDropdownItem, BLink, BTooltip,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { $themeBreakpoints } from '@themeConfig'
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app'
import CustomerForm from '@/views/organization/customers/CustomerForm'
import CustomerView from '@/views/organization/customers/CustomerView'
import RcOverlay from '@/layouts/components/rc/RcOverlay'
import role from '@/auth/role'
import dayjs from 'dayjs'
import NotificationTypeDictionary from '@/dictionary/NotificationTypeDictionary'
import { isAnonymizedCustomer, parseRequestError } from '@/helpers/helpers'
import SendMessageModal from '@/views/organization/customers/SendMessageModal.vue'
import CustomerLeftSidebar from './CustomerLeftSidebar.vue'

export default {
  components: {
    CustomerView,
    CustomerForm,

    RcOverlay,
    SendMessageModal,

    // BSV
    BAvatar,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BLink,
    BTooltip,
    BButton,
    // 3rd Party
    VuePerfectScrollbar,

    // SFC
    CustomerLeftSidebar,
  },
  computed: {
    customers() {
      return this.$store.getters['customer/getCustomers']
    },
    customer() {
      return this.$store.getters['customer/getCustomer']
    },
    currentCustomerInitials() {
      return this.customer.firstName.charAt(0) + this.customer.lastName.charAt(0)
    },
    statistics() {
      return this.$store.getters['customer/customerStatistics']
    },
    me() {
      return this.$store.getters['auth/getUser']
    },
    role() {
      return role
    },
  },
  beforeMount() {
    this.$store.dispatch('customer/fetchCustomers')
    if (this.$route.params.customerId) {
      this.loading = true
      this.$store.commit('customer/SET_CUSTOMER', {})
      this.$store.dispatch(
        'customer/fetchCustomer',
        this.$route.params.customerId,
      )
        .then(response => {
          if (isAnonymizedCustomer(response.data)) {
            window.toast.notify.warning(this.$t('customer.gdpr.profile_unavailable'))
            this.$store.dispatch('customer/resetCustomer')
            window.history.pushState(null, null, '/organization/customers')
            this.loading = false

            return
          }

          this.loading = false
        })
        .catch(err => {
          if (err?.response?.data?.message === 'customer.gdpr.profile_unavailable') {
            window.toast.notify.warning(this.$t('customer.gdpr.profile_unavailable'))
            this.$store.dispatch('customer/resetCustomer')
            window.history.pushState(null, null, '/organization/customers')
            this.loading = false

            return
          }

          this.loading = false
          parseRequestError(err)
        })
    }
  },
  setup(props, { root }) {
    const store = root.$store
    const router = root.$router
    const i18n = root.$i18n
    const refScroll = ref(null)
    const atLeastOneCustomerLoaded = ref(false)
    const loading = ref(false)
    const addingCustomer = ref(false)
    const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()
    const invitationButtonClicked = ref(false)

    const resetCustomerView = () => {
      loading.value = false
      invitationButtonClicked.value = false
    }

    const editMode = ref(router.currentRoute.name === 'customers-edit')
    const toggleEditMode = () => {
      editMode.value = !editMode.value
      window.history.pushState(null, null, `/organization/customers/${store.getters['customer/getCustomer'].id}/${editMode.value === true ? 'edit' : 'view'}`)
    }

    const openCustomerView = customerId => {
      if (router.currentRoute.name === 'customers-view' && customerId === store.getters['customer/getCustomer'].id) {
        return
      }

      editMode.value = false
      window.history.pushState(null, null, `/organization/customers/${customerId}/view`)
      loading.value = true
      store.commit('customer/SET_CUSTOMER', {})
      store.dispatch('customer/fetchCustomer', customerId)
        .then(() => {
          nextTick(() => { resetCustomerView() })
        })

      // if SM device =>  Close Chat & Contacts left sidebar
      // eslint-disable-next-line no-use-before-define
      mqShallShowLeftSidebar.value = false
    }

    const initAddCustomer = () => {
      addingCustomer.value = true
      editMode.value = true
      store.dispatch('customer/resetCustomer')
      mqShallShowLeftSidebar.value = false
    }

    const customerAddedHandler = customer => {
      openCustomerView(customer.id)
      addingCustomer.value = false
      store.dispatch('customer/fetchCustomers')
    }

    const customerSavedHandler = customer => {
      if (!customer.archived) {
        return
      }
      store.dispatch('customer/resetCustomer')
      store.dispatch('customer/fetchCustomers')
      addingCustomer.value = false
      editMode.value = false
      window.history.pushState(null, null, '/organization/customers')
    }

    const customerAnonymizedHandler = () => {
      store.dispatch('customer/resetCustomer')
      store.dispatch('customer/fetchCustomers')
      addingCustomer.value = false
      editMode.value = false
      window.history.pushState(null, null, '/organization/customers')
    }

    const inviteButtonVisible = computed(() => {
      const c = store.getters['customer/getCustomer']
      const weekAgo = dayjs().subtract(1, 'week')
      const invitedOk = !c.invitedAt || dayjs(c.invitedAt).isBefore(weekAgo)
      const hasEmail = c.email || c.guardianEmail
      return hasEmail && !c.registered && invitedOk && !invitationButtonClicked.value
    })

    const hasBothEmails = computed(() => {
      const c = store.getters['customer/getCustomer']
      return c.email && c.guardianEmail
    })

    const defaultInviteType = computed(() => {
      const c = store.getters['customer/getCustomer']
      if (c.email) return 'Student'
      if (c.guardianEmail) return 'Guardian'
      return null
    })

    const inviteCustomer = type => {
      if (!type) return
      invitationButtonClicked.value = true
      store.dispatch('customer/inviteCustomer', {
        customerId: store.getters['customer/getCustomer'].id,
        type,
      })
        .then(() => {
          window.toast.notify.success(i18n.t('customers.invited-success'))
        })
        .catch(() => {
          window.toast.notify.error(i18n.t('customers.invited-error'))
          invitationButtonClicked.value = false
        })
    }

    const perfectScrollbarSettings = {
      maxScrollbarLength: 150,
      suppressScrollX: true,
    }

    const largeScreen = computed(() => store.state.app.windowWidth >= $themeBreakpoints.lg)

    // UI + SM Devices
    // Left Sidebar Responsiveness
    if (router.currentRoute.name === 'customers' && store.state.app.windowWidth < $themeBreakpoints.lg) {
      mqShallShowLeftSidebar.value = true
    }
    const startConversation = () => {
      if (store.state.app.windowWidth < $themeBreakpoints.lg) {
        mqShallShowLeftSidebar.value = true
      }
    }

    const updateRefScroll = () => {
      if (!refScroll.value) {
        return
      }

      refScroll.value.update()
    }

    const messageReceivers = computed(() => {
      const receivers = []
      const c = store.getters['customer/getCustomer']
      if (c.email) {
        receivers.push({ type: 'Student', label: 'customers.send-message.receiver.student' })
      }
      if (c.guardianEmail) {
        receivers.push({ type: 'Guardian', label: 'customers.send-message.receiver.guardian' })
      }
      return receivers
    })

    const canSendCustomerMessage = computed(() => messageReceivers.value.length > 0 && !!store.getters['customer/getCustomer'].id)
    const defaultMessageReceiver = computed(() => (messageReceivers.value.length ? messageReceivers.value[0].type : null))
    const singleMessageReceiverType = computed(() => (messageReceivers.value.length === 1 ? messageReceivers.value[0].type : null))
    const sendMessageButtonId = computed(() => {
      const currentId = store.getters['customer/getCustomer'].id
      return `send-message-dropdown-trigger-${currentId || 'none'}`
    })
    const sendingPaymentReminder = ref(false)
    const isSendMessageModalVisible = ref(false)
    const customMessageDefaultReceiver = ref(null)

    const sendPaymentReminder = receiverType => {
      if (sendingPaymentReminder.value || !canSendCustomerMessage.value) {
        return
      }
      const customerId = store.getters['customer/getCustomer'].id
      const targetReceiver = receiverType || defaultMessageReceiver.value
      if (!customerId || !targetReceiver) {
        return
      }
      sendingPaymentReminder.value = true
      store.dispatch('customer/sendCustomerMessage', {
        customerId,
        payload: {
          notificationType: NotificationTypeDictionary.PaymentReminder,
          receiverType: targetReceiver,
        },
      })
        .then(() => {
          window.toast.notify.success(i18n.t('customers.send-message.success'))
        })
        .catch(() => {
          window.toast.notify.error(i18n.t('customers.send-message.error'))
        })
        .finally(() => {
          sendingPaymentReminder.value = false
        })
    }

    const showCustomMessageModal = receiverType => {
      if (!canSendCustomerMessage.value) {
        return
      }
      customMessageDefaultReceiver.value = receiverType || defaultMessageReceiver.value
      isSendMessageModalVisible.value = true
    }

    return {
      refScroll,
      atLeastOneCustomerLoaded,
      loading,
      addingCustomer,

      openCustomerView,
      toggleEditMode,
      customerAddedHandler,
      customerAnonymizedHandler,
      customerSavedHandler,
      initAddCustomer,

      inviteCustomer,
      inviteButtonVisible,
      hasBothEmails,
      defaultInviteType,
      messageReceivers,
      canSendCustomerMessage,
      singleMessageReceiverType,
      sendingPaymentReminder,
      sendPaymentReminder,
      showCustomMessageModal,
      isSendMessageModalVisible,
      customMessageDefaultReceiver,
      sendMessageButtonId,

      // UI
      editMode,
      perfectScrollbarSettings,
      updateRefScroll,

      // UI + SM Devices
      startConversation,
      mqShallShowLeftSidebar,
      largeScreen,
    }
  },
}
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
@import "../../../assets/scss/base/pages/app-customers";
@import "../../../assets/scss/base/pages/app-customers-list";
</style>
